import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { NoData } from 'Components/NoData';
import { PageHeader, Button } from 'Components/ui';

import { getPoliciesUserByPolicyIdAndPolicyVersionId, getPolicyAcceptsSummary } from 'services/policies-service';
import { formatDateTimeToDateTimeBR, formatDateToDateBRPolish } from 'utils/date_utils';

const BLUE = 'var(--color-brand)';
const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const DARK_TEXT = 'var(--vo-ink-2)';
const CARD_SHADOW = '0 1px 3px var(--vo-escuro-8)';

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 30px 0;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: ${CARD_SHADOW};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--vo-info-10);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CountText = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${BLUE};
  margin: 0 0 2px;
  letter-spacing: -0.01em;
`;

const SubText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
`;

const AcceptsUnit = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledThead = styled.thead`
  background: var(--vo-mist);
`;

const StyledTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;

const StyledTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  transition: background 0.12s;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const StyledTd = styled.td`
  padding: 12px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarChip = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Rubik', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
`;

const EmailText = styled.span`
  color: ${MUTED};
`;

const DateText = styled.span`
  color: ${DARK_TEXT};
`;

const NotReadText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-steel);
  font-style: italic;
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--vo-mist);
`;

const FooterCount = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

const getInitials = name => {
  if (!name) return '??';
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const PAGE_SIZE = 6;

export const PoliciesShowAccepts = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  const { data: policyVersions, isLoading } = useQuery(
    ['getPoliciesUserByPolicyIdAndPolicyVersionId', id, idVersion],
    async () => {
      const res = await getPoliciesUserByPolicyIdAndPolicyVersionId(id, idVersion);
      if (res.status === 200 && res.data.length > 0) return res.data;
      return null;
    }
  );

  // Same {sent, accepted} summary already surfacing on the version's Accepts Counter card
  // (Versions/Show) — the header here showed a single ambiguous "N accepts" count before.
  const { data: acceptsSummary } = useQuery(
    ['getPolicyAcceptsSummary', id, idVersion],
    async () => {
      const res = await getPolicyAcceptsSummary(id, idVersion);
      return res.status === 200 ? res.data : { sent: 0, accepted: 0 };
    }
  );

  const policyName = policyVersions?.[0]?.policyName ?? '';
  const policyVersion = policyVersions?.[0]?.policyVersion ?? '';
  const publishedDate = policyVersions?.[0]?.policyPublishedDate;
  const total = policyVersions?.length ?? 0;
  const visibleRows = policyVersions?.slice(0, displayCount) ?? [];

  const handleExportCsv = () => {
    if (!policyVersions) return;
    const headers = [
      t('policy:accepts.table.user'),
      t('policy:accepts.table.email'),
      t('policy:accepts.table.forwarded'),
      t('policy:accepts.table.firstRead'),
      t('policy:accepts.table.accepted'),
    ];
    const rows = policyVersions.map(item => [
      `"${item?.user?.name ?? ''}"`,
      `"${item?.user?.email ?? ''}"`,
      `"${formatDateTimeToDateTimeBR(item.sentDate, true)}"`,
      `"${formatDateTimeToDateTimeBR(item.readedDate, true)}"`,
      `"${formatDateTimeToDateTimeBR(item.acceptedDate, true)}"`,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aceites-${policyName}-v${policyVersion}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      {isLoading && <PageLoaderShow />}
      {!isLoading && policyVersions && (
        <PageWrap>
          <PageHeader
            title={t('policy:accepts.acceptsTitle')}
            segments={[
              { label: t('common:breadcrumbs.compliance') },
              { label: t('policy:page.title'), route: '/policies' },
              { label: policyName, route: `/policies/show/${id}` },
              { label: `${t('policy:versionShow.versionLabel')} ${policyVersion}`, route: `/policies/show/${id}/versions/${idVersion}` },
              { label: t('policy:accepts.acceptsTitle') },
            ]}
          />

          <SummaryCard>
            <SummaryLeft>
              <IconBox>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </IconBox>
              <div>
                <CountText data-testid="policy-accepts-header-summary">
                  {acceptsSummary?.sent ?? 0} <AcceptsUnit>({t('policy:versionShow.accepts.sent')})</AcceptsUnit>
                  {' / '}
                  {acceptsSummary?.accepted ?? 0} <AcceptsUnit>({t('policy:versionShow.accepts.accepted')})</AcceptsUnit>
                </CountText>
                <SubText>
                  {t('policy:accepts.subtitle', {
                    name: policyName,
                    version: policyVersion,
                    date: formatDateToDateBRPolish(publishedDate),
                  })}
                </SubText>
              </div>
            </SummaryLeft>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              startIcon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              }
              sx={{ height: '36px', padding: '0 14px', fontSize: '12px' }}
            >
              {t('policy:accepts.exportCsv')}
            </Button>
          </SummaryCard>

          <TableCard>
            <StyledTable>
              <StyledThead>
                <tr>
                  <StyledTh>{t('policy:accepts.table.user')}</StyledTh>
                  <StyledTh>{t('policy:accepts.table.email')}</StyledTh>
                  <StyledTh>{t('policy:accepts.table.forwarded')}</StyledTh>
                  <StyledTh>{t('policy:accepts.table.firstRead')}</StyledTh>
                  <StyledTh>{t('policy:accepts.table.accepted')}</StyledTh>
                </tr>
              </StyledThead>
              <tbody>
                {visibleRows.map((item, idx) => (
                  <StyledTr key={idx}>
                    <StyledTd>
                      <UserCell>
                        <AvatarChip>{getInitials(item?.user?.name)}</AvatarChip>
                        <UserName>{item?.user?.name}</UserName>
                      </UserCell>
                    </StyledTd>
                    <StyledTd>
                      <EmailText>{item?.user?.email ?? '—'}</EmailText>
                    </StyledTd>
                    <StyledTd>
                      <DateText>{formatDateTimeToDateTimeBR(item.sentDate, true)}</DateText>
                    </StyledTd>
                    <StyledTd>
                      {item.readedDate
                        ? <DateText>{formatDateTimeToDateTimeBR(item.readedDate, true)}</DateText>
                        : <NotReadText>{t('policy:accepts.notReadBadge')}</NotReadText>}
                    </StyledTd>
                    <StyledTd>
                      <DateText>{formatDateTimeToDateTimeBR(item.acceptedDate, true)}</DateText>
                    </StyledTd>
                  </StyledTr>
                ))}
              </tbody>
            </StyledTable>
            <TableFooter>
              <FooterCount>
                {t('policy:accepts.showing', { count: Math.min(displayCount, total), total })}
              </FooterCount>
              {displayCount < total && (
                <Button variant="outline" onClick={() => setDisplayCount(c => c + PAGE_SIZE)} sx={{ height: '36px', padding: '0 18px' }}>
                  {t('policy:accepts.loadMore')}
                </Button>
              )}
            </TableFooter>
          </TableCard>
        </PageWrap>
      )}
      {!isLoading && !policyVersions && (
        <PageWrap>
          <NoData description={t('policy:accepts.noData')} />
        </PageWrap>
      )}
    </Container>
  );
};
